import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { partsAPI } from '../../services/api';
import { Card, CardContent, CardHeader } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from '../../components/ui/dialog';
import { Plus, Search, Calculator, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import WireCostingDialog from '../../components/costings/WireCostingDialog';

const fmt = (n, ccy = 'INR', dec = 2) => {
  const sym = ccy === 'INR' ? '₹' : ccy === 'USD' ? '$' : ccy === 'EUR' ? '€' : `${ccy} `;
  return `${sym}${Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: dec, maximumFractionDigits: dec })}`;
};
const fmtDate = (s) => { if (!s) return '-'; try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; } };
const statusColor = (s) => {
  const x = String(s || '').toLowerCase();
  if (['approved', 'locked'].includes(x)) return 'bg-emerald-100 text-emerald-700';
  if (['rejected'].includes(x)) return 'bg-red-100 text-red-700';
  if (['draft'].includes(x)) return 'bg-slate-100 text-slate-700';
  return 'bg-blue-100 text-blue-700';
};

export default function WireCostingsPage() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [newDialogOpen, setNewDialogOpen] = useState(false);
  const [wireParts, setWireParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [wcDialogOpen, setWcDialogOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get('/wire-costings', { params: { search: search || undefined } })
      .then((r) => setRows(r.data.items || []))
      .catch(() => toast.error('Failed to load wire costings'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search]); // eslint-disable-line

  const openNew = async () => {
    setNewDialogOpen(true);
    if (wireParts.length === 0) {
      try {
        const r = await partsAPI.list({ product_category: 'wire', limit: 500 });
        setWireParts(r.data.items || r.data.data || []);
      } catch { toast.error('Failed to load wire parts'); }
    }
  };

  const handlePartPick = (part) => {
    setNewDialogOpen(false);
    setSelectedPart(part);
    setWcDialogOpen(true);
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="wire-costings-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-heading font-bold uppercase tracking-tight flex items-center gap-3">
            <Calculator className="h-7 w-7 text-blue-600" />
            Wire Costings
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Per-kg costings (RM + PPD + SAPPD + Packing + Overheads% of process + Margin)</p>
        </div>
        <Button className="btn-primary" onClick={openNew} data-testid="add-wire-costing-btn">
          <Plus className="h-4 w-4 mr-2" />New Wire Costing
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search by costing #, part, grade, customer…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="wc-search" />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Costing #</TableHead>
                  <TableHead>Part</TableHead>
                  <TableHead>Grade</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Total / Kg</TableHead>
                  <TableHead className="text-right">SP / Kg</TableHead>
                  <TableHead className="text-right">SP / Ton</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((c) => (
                  <TableRow key={c.id} className="hover:bg-muted/40" data-testid={`wc-row-${c.id}`}>
                    <TableCell className="font-mono font-medium">{c.costing_number}</TableCell>
                    <TableCell className="font-mono text-xs">{c.part_internal_no}</TableCell>
                    <TableCell>{c.wire_grade || '-'}</TableCell>
                    <TableCell>{c.customer_name || '-'}</TableCell>
                    <TableCell><Badge className={statusColor(c.status)} variant="outline">{c.status}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{fmt(c.total_cost_per_kg, c.currency)}</TableCell>
                    <TableCell className="text-right font-mono font-semibold">{fmt(c.selling_price_per_kg, c.currency)}</TableCell>
                    <TableCell className="text-right font-mono text-muted-foreground">{fmt(c.selling_price_per_ton, c.currency, 0)}</TableCell>
                    <TableCell className="text-xs">{fmtDate(c.created_at)}</TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => navigate(`/costings/${c.id}`)}><Eye className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">No wire costings yet. Click <strong>New Wire Costing</strong> to create one.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Part picker dialog */}
      <Dialog open={newDialogOpen} onOpenChange={setNewDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Select Wire Part</DialogTitle>
            <DialogDescription>Pick a wire part to begin a new costing.</DialogDescription>
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto space-y-1">
            {wireParts.length === 0 ? (
              <p className="text-sm text-muted-foreground p-4 text-center">No wire parts yet — go to Wire Parts to add one.</p>
            ) : wireParts.map((p) => (
              <button
                key={p.id}
                onClick={() => handlePartPick(p)}
                className="w-full text-left p-3 rounded-md hover:bg-muted/60 border border-transparent hover:border-border flex items-center justify-between"
                data-testid={`pick-wire-part-${p.id}`}
              >
                <div>
                  <p className="font-mono font-medium">{p.internal_part_no}</p>
                  <p className="text-xs text-muted-foreground">{p.description}</p>
                </div>
                <div className="text-right text-xs text-muted-foreground">
                  <p>{p.wire_diameter_min_mm} – {p.wire_diameter_max_mm} mm</p>
                  <p>{p.wire_grade}</p>
                </div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Wire Costing dialog */}
      <WireCostingDialog
        open={wcDialogOpen}
        onOpenChange={(o) => { setWcDialogOpen(o); if (!o) load(); }}
        part={selectedPart}
        onCreated={load}
      />
    </div>
  );
}
